import React from 'react';
import { AlertCircle, CheckCircle, Info, XCircle, Bell, Check, Clock, X } from 'lucide-react';
import { apiFetch } from '../api';

export interface InAppNotification {
  id?: number;
  ruleId?: number;
  eventType?: string;
  event_type_code?: string;
  title: string;
  message: string;
  type: 'toast' | 'modal';
  severity: 'info' | 'success' | 'warning' | 'error';
  sound?: boolean;
  createdAt?: string;
  created_at?: string;
}

interface InAppNotificationModalProps {
  notification: InAppNotification | null;
  onDismiss: () => void;
}

export const InAppNotificationModal: React.FC<InAppNotificationModalProps> = ({
  notification,
  onDismiss,
}) => {
  if (!notification) return null;

  const handleConfirm = async () => {
    onDismiss();
    if (notification.id) {
      try {
        await apiFetch(`/system-notifications/inapp/${notification.id}/dismiss`, {
          method: 'POST',
        });
      } catch (e) {
        console.error('Fehler beim Bestätigen der Benachrichtigung:', e);
      }
    }
  };

  const getSeverityConfig = (sev: string) => {
    switch (sev) {
      case 'success':
        return {
          cardBorder: 'border-emerald-200 dark:border-emerald-800/80 shadow-emerald-500/10',
          gradientBg: 'from-emerald-600 via-teal-600 to-emerald-700',
          badgeBg: 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30',
          badgeText: 'Erfolg & Bestätigung',
          accentText: 'text-emerald-700 dark:text-emerald-300',
          accentBg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900/50',
          btnBg: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-600/30',
          iconContainer: 'bg-white/15 ring-1 ring-white/30 text-white',
          icon: <CheckCircle className="w-6 h-6 text-white shrink-0" />,
        };
      case 'warning':
        return {
          cardBorder: 'border-amber-200 dark:border-amber-800/80 shadow-amber-500/10',
          gradientBg: 'from-amber-500 via-amber-600 to-orange-600',
          badgeBg: 'bg-amber-400/20 text-amber-100 border border-amber-300/30',
          badgeText: 'Wichtiger Hinweis',
          accentText: 'text-amber-800 dark:text-amber-300',
          accentBg: 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-200/60 dark:border-amber-900/40',
          btnBg: 'bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-400 hover:to-orange-500 shadow-amber-600/30',
          iconContainer: 'bg-white/15 ring-1 ring-white/30 text-white',
          icon: <AlertCircle className="w-6 h-6 text-white shrink-0" />,
        };
      case 'error':
        return {
          cardBorder: 'border-rose-200 dark:border-rose-800/80 shadow-rose-500/10',
          gradientBg: 'from-rose-600 via-rose-700 to-red-700',
          badgeBg: 'bg-rose-500/20 text-rose-100 border border-rose-400/30',
          badgeText: 'Dringende Meldung',
          accentText: 'text-rose-800 dark:text-rose-300',
          accentBg: 'bg-rose-50/70 dark:bg-rose-950/30 border-rose-200/60 dark:border-rose-900/40',
          btnBg: 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 shadow-rose-600/30',
          iconContainer: 'bg-white/15 ring-1 ring-white/30 text-white',
          icon: <XCircle className="w-6 h-6 text-white shrink-0" />,
        };
      default:
        return {
          cardBorder: 'border-blue-200 dark:border-blue-800/80 shadow-blue-500/10',
          gradientBg: 'from-blue-600 via-indigo-600 to-blue-700',
          badgeBg: 'bg-blue-500/20 text-blue-100 border border-blue-400/30',
          badgeText: 'Systembenachrichtigung',
          accentText: 'text-blue-800 dark:text-blue-300',
          accentBg: 'bg-blue-50/70 dark:bg-blue-950/30 border-blue-200/60 dark:border-blue-900/40',
          btnBg: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-blue-600/30',
          iconContainer: 'bg-white/15 ring-1 ring-white/30 text-white',
          icon: <Info className="w-6 h-6 text-white shrink-0" />,
        };
    }
  };

  const cfg = getSeverityConfig(notification.severity);
  const timeString = notification.createdAt || notification.created_at
    ? new Date(notification.createdAt || notification.created_at!).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : null;

  const eventCode = notification.eventType || notification.event_type_code;
  const eventLabel = eventCode === 'incident_report' ? 'Ereignis- & Personalmeldung' : eventCode;

  return (
    <div
      className="fixed inset-0 bg-black/65 backdrop-blur-md flex items-center justify-center z-[99999] p-4 transition-all duration-300 animate-in fade-in"
      role="dialog"
      aria-modal="true"
    >
      <div className={`w-full max-w-lg rounded-3xl shadow-2xl border ${cfg.cardBorder} overflow-hidden bg-white dark:bg-slate-900 transform transition-all duration-200`}>
        {/* Header Banner */}
        <div className={`bg-gradient-to-r ${cfg.gradientBg} p-5 sm:p-6 text-white relative overflow-hidden`}>
          {/* Subtle background glow effect */}
          <div className="absolute -top-12 -right-12 w-36 h-36 bg-white/10 rounded-full blur-2xl pointer-events-none" />

          <div className="flex items-start justify-between gap-4 relative z-10">
            <div className="flex items-start gap-3.5">
              <div className={`p-2.5 rounded-2xl ${cfg.iconContainer} shadow-sm backdrop-blur-md flex items-center justify-center`}>
                {cfg.icon}
              </div>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide uppercase backdrop-blur-sm shadow-sm leading-tight">
                  <span className={cfg.badgeBg}>
                    {cfg.badgeText}
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-bold leading-snug drop-shadow-sm pr-2">
                  {notification.title || 'Systemmeldung'}
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {timeString && (
                <span className="inline-flex items-center gap-1 text-[11px] bg-black/20 text-white/90 border border-white/20 px-2.5 py-1 rounded-full font-medium tracking-tight">
                  <Clock className="w-3 h-3 text-white/80" />
                  {timeString}
                </span>
              )}
              <button
                onClick={handleConfirm}
                className="p-1 rounded-full text-white/70 hover:text-white hover:bg-white/20 transition"
                title="Schließen"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-4">
          <div className="text-gray-800 dark:text-slate-100 text-sm sm:text-base leading-relaxed whitespace-pre-line font-medium">
            {notification.message}
          </div>

          {eventCode && (
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold ${cfg.accentBg} ${cfg.accentText}`}>
              <Bell className="w-3.5 h-3.5 opacity-80" />
              <span>Bereich: <strong className="font-bold">{eventLabel}</strong></span>
            </div>
          )}
        </div>

        {/* Action Footer */}
        <div className="px-6 py-4 bg-gray-50/80 dark:bg-slate-900/80 border-t border-gray-100 dark:border-slate-800 flex items-center justify-end">
          <button
            onClick={handleConfirm}
            className={`px-5 py-2.5 rounded-2xl text-white text-xs sm:text-sm font-bold flex items-center gap-2 shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-95 cursor-pointer ${cfg.btnBg}`}
          >
            <Check className="w-4 h-4" strokeWidth={2.5} />
            Verstanden &amp; Bestätigen
          </button>
        </div>
      </div>
    </div>
  );
};

export default InAppNotificationModal;
